import { createContext, useEffect } from 'react';
import { useImmerReducer } from "use-immer";
import { DISPATCH as TYPE } from "consts/chat.consts";
import * as SCOPE from "services/sync/scope";
import { on } from "infrastructure/eventHub";
import useInfrastructure from 'hooks/useInfrastructure.hook';
import { useChatPendingMessagesCount } from 'hooks/chat/useChatPendingMessagesCount.hook';
import useAuthentication from 'hooks/useAuthentication.hook';
import { useChatConversationSelected } from 'hooks/chat/useChatConversationSelected.hook';
import { useChatLineSelected } from 'hooks/chat/useChatLineSelected.hook';
import { useChatAgentSelected } from 'hooks/chat/useChatAgentSelected.hook';
import newConversationSound from 'design/assets/sounds/cta/new-conversation.wav';
import newMessageSound from 'design/assets/sounds/cta/new-message.wav';

const ChatContext = createContext();
export default ChatContext;

const reducer = (state, action) => {
  switch (action.type) {
    case TYPE.LINE.SELECTED.TYPE: {
      const lastLineSelectedId = state.selected.lineId;
      state.selected.lineId = action.payload;
      if(state.selected.lineId !== lastLineSelectedId) {
        state.selected.agentId = null;
        state.selected.conversationId = null;
      }
      break;
    }
    case TYPE.AGENT.SELECTED.TYPE: {
      const lastAgentSelectedId = state.selected.agentId;
      state.selected.agentId = action.payload;
      if(state.selected.agentId !== lastAgentSelectedId) {
        state.selected.conversationId = null;
      }
      break;
    }
    case TYPE.CONVERSATION.SELECTED.TYPE: {
      state.selected.conversationId = action.payload;
      break;
    }
    default:
      throw Error(`Unknown chat action: ${action.type}`);
  }
};

export function ChatContextProvider(props) {
  const { syncEngine, repositories } = useInfrastructure();
  const { user } = useAuthentication();
  const [state, dispatch] = useImmerReducer(reducer, {
    selected: {
      lineId: null,
      agentId: null,
      conversationId: null
    }
  });

  const lineSelected = useChatLineSelected(state.selected.lineId);
  const agentSelected = useChatAgentSelected(state.selected.agentId);
  const conversationSelected = useChatConversationSelected(state.selected.conversationId);
  const pending = useChatPendingMessagesCount();
  
  const invalidateAllSync = () => {
    Object.keys(SCOPE.CHAT).forEach(key => {
      syncEngine.invalidate(SCOPE.CHAT[key]);
    });
  }

  const mergeMessageByTempId = async (message) => {
    let replaced = false;
    
    // Proceso de merge en caso de que el mensaje llegue antes que el ack del mensaje enviado
    const { tempId } = message;
    if (tempId) {
      const messageFound = await repositories.events.getById(tempId);
      if (messageFound) {
        replaced = true;
        repositories.events.replaceByKey({
          index: "tempId",
          value: tempId,
          newItem: message
        });
      }
    }

    return replaced;
  };

  useEffect(() => {
    document.title = pending > 0
      ? `(${pending}) NeoConecta`
      : "NeoConecta";
  }, [pending]);
  
  useEffect(() => {
    const unsubTabLeader = on("infrastructure", "leader", "became", invalidateAllSync);

    const unsubSocketRecovered = on("socket", "connection", "recovered", () => {
      invalidateAllSync();

      const actives = syncEngine.activeScopes.getAll();
      Promise.allSettled(actives.map(({ syncEngine, scope, repository, repositories, syncFn, syncParams }) => (
        syncFn({
          syncEngine,
          scope,
          repository,
          repositories,
          params: syncParams,
          force: true
        })
      ))).then(results => {
        results.forEach((result, index) => {
          if (result.status === "rejected") {
            const { scope } = actives[index];
            console.error("Error syncing scope:", scope, result.reason);
          }
        });
      });
    });

    const unsubConversationAssigned = on("chat", "conversation", "assigned", ({ payload }) => {
      const { conversation } = payload;
      repositories.conversations.upsert(conversation);

      // Invalidate events cache for this conversation
      syncEngine.invalidate(SCOPE.CHAT.EVENTS, `(lineId:${conversation.lineId})-(chatId:${conversation.chatId})`);
    });

    const unsubConversationUpdated = on("chat", "conversation", "updated", ({ payload }) => {
      const { conversation } = payload;
      repositories.conversations.patch(conversation.id, (currentConversation) => {
        if (currentConversation
          && currentConversation?.newMessages < conversation?.newMessages
          && user.role === "AGENT" // ToDo: Mover esta lógica a un hook específico para reproducir el sonido solo a los agentes
        ) {
          new Audio(newMessageSound).play();
        }
        return conversation;
      });
    });

    const unsubConversationClose = on("chat", "conversation", "close", ({ payload }) => {
      const { conversation } = payload;
      repositories.conversations.delete(conversation.id);
    });

    const unsubMessageSent = on("chat", "message", "sent", async ({ payload }) => {
      const { message } = payload;
      const replaced = await mergeMessageByTempId(message);
      if (!replaced) {
        repositories.events.upsert(message);
      }
    });

    const unsubMessageStatusUpdate = on("chat", "message", "statusUpdate", async ({ payload }) => {
      const { message } = payload;
      const replaced = await mergeMessageByTempId(message);
      if (!replaced) {
        repositories.events.patch(message.id, message);
      }
    });

    const unsubMessageReceived = on("chat", "message", "received", ({ payload }) => {
      const { message } = payload;
      repositories.events.upsert(message);
    });

    const unsubCommentAdd = on("chat", "comment", "add", ({ payload }) => {
      const { comment } = payload;
      repositories.events.upsert(comment);
    });

    const unsubEventAdd = on("chat", "event", "add", ({ payload }) => {
      const { event } = payload;
      repositories.events.upsert(event);
    });

    const unsubExpiredConversationAssigned = on("chat", "expiredConversation", "assigned", ({ payload }) => {
      const { conversation } = payload;
      repositories.conversations.delete(conversation.id);
    });

    const unsubUnassignedConversationAdd = on("chat", "unassignedConversation", "add", ({ payload }) => {
      const { conversation } = payload;
      repositories.conversations.upsert(conversation);
    });

    const unsubUnassignedConversationAssigned = on("chat", "unassignedConversation", "assigned", ({ payload }) => {
      const { conversation } = payload;
      repositories.conversations.delete(conversation.id);
    });

    return () => {
      unsubSocketRecovered();
      unsubTabLeader();
      unsubConversationAssigned();
      unsubConversationUpdated();
      unsubConversationClose();
      unsubMessageSent();
      unsubMessageStatusUpdate();
      unsubMessageReceived();
      unsubCommentAdd();
      unsubEventAdd();
      unsubExpiredConversationAssigned();
      unsubUnassignedConversationAdd();
      unsubUnassignedConversationAssigned();
    };
  }, []);

  useEffect(() => {
    const userLogged = user;
    return on("chat", "conversation", "assigned", ({ payload }) => {
      const { user } = payload;
      if (userLogged.role === "AGENT" && userLogged.uid === user.id) {
        new Audio(newConversationSound).play();
      }
    });
  }, [user.uid, user.role])

  useEffect(() => {
    return on("chat", "conversation", "close", ({ payload }) => {
      const { conversation } = payload;
      if (state.selected.conversationId && state.selected.conversationId === conversation.id) {
        dispatch({
          type: TYPE.CONVERSATION.SELECTED.TYPE,
          payload: null
        });
      }
    });
  }, [state.selected.conversationId]);

  useEffect(() => {
    return on("chat", "message", "received", ({ payload }) => {
      const { message, callback } = payload;

      const isAgent = user.role === 'AGENT';
      const isCurrentConversation =
        state.selected.conversationId
        && state.selected.conversationId === message.conversationId;
      
      if (isAgent && isCurrentConversation && typeof callback === "function") {
        callback({
          event: {
            message: {
              read: true
            }
          }
        });
      }
    });
  }, [user.role, state.selected.conversationId]);
  
  return (
    <ChatContext.Provider value={{
      state,
      dispatch,
      selected: {
        line: lineSelected,
        conversation: conversationSelected,
        agent: agentSelected,
      }
    }}
    >
      {props.children}
    </ChatContext.Provider>
  );
}